import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import TreeItem, { treeItemClasses } from '@mui/lab/TreeItem';
import { CustomInput } from '../../../../components/CustomInput';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch, useSelector } from 'react-redux';
import { createBranch, createDepartment, getDepartmentsOfBranch } from '../../../../store/company/actions';
import AddIcon from '@mui/icons-material/Add';

const StyledTreeItemRoot = styled(TreeItem)(({ theme }) => ({
  minHeight: 52,
  width: '100%',

  [`& .${treeItemClasses.content}`]: {
    height: 52,
    width: '100%',
    border: `1px solid ${theme.palette.action.disabled}`,
    borderRadius: 12,
    background: theme.palette.action.default,
    padding: '14px 9px 14px 4px',
    marginBottom: 8,
    '& .Mui-selected.Mui-focused': {
      backgroundColor: theme.palette.action.default
    },

    '& .Mui-selected': {
      backgroundColor: theme.palette.action.default
    },

    '& .Mui-expanded.Mui-focused': {
      backgroundColor: theme.palette.action.default
    },

    '& .MuiTreeItem-content:hover': {
      background: 'none'
    },

    '& > .Mui-selected:hover': {
      background: 'none'
    },
    '& .MuiTreeItem-iconContainer': {
      display: 'none'
    },
    [`& .${treeItemClasses.label}`]: {
      fontWeight: 'inherit',
      color: 'inherit'
    }
  }
}));

export const CustomAddTreeItem = ({ label, type, branchId, ...other }) => {
  const dispatch = useDispatch();
  const [isEdit, setIsEdit] = useState(false);
  const [currentLabel, setCurrentLabel] = useState(label);

  const companyState = useSelector((state) => state.company);
  const { companyInfo } = companyState;

  const placeholder =
    type === 'branch' ? 'Введите название филиала' : 'Введите название департамента';
  const addText = type === 'branch' ? 'Добавить филиал' : 'Добавить департамент';

  const handleSuccess = (e) => {
    e.stopPropagation();
    if (type === 'branch') {
      dispatch(createBranch({ name: currentLabel, company_id: companyInfo.id }));
    } else {
      dispatch(createDepartment({ name: currentLabel, branch_id: branchId }));
      dispatch(getDepartmentsOfBranch(branchId));
    }

    setIsEdit(false);
  };

  return (
    <StyledTreeItemRoot
      label={
        <Box sx={{ display: 'flex', alignItems: 'center', p: 0.5, pr: 0, position: 'relative' }}>
          {isEdit ? (
            <Box sx={{ fontWeight: 'inherit', flexGrow: 1, display: 'flex', alignItems: 'center' }}>
              <CustomInput
                sx={{ display: 'flex', width: '70%', p: 0.5, pr: 0 }}
                size="small"
                value={currentLabel}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  const input = e.target.value || '';
                  if (input.length <= 50) {
                    setCurrentLabel(input);
                  }
                }}
                placeholder={placeholder}
              />
              <Box ml={4} display="flex" alignItems="center">
                <CheckIcon
                  sx={{ marginRight: '16px' }}
                  color={currentLabel?.length > 1 ? 'success' : 'disabled'}
                  cursor={currentLabel?.length > 1 ? 'pointer' : 'not-allowed'}
                  disabled={currentLabel?.length < 2}
                  onClick={(e) => currentLabel?.length > 1 && handleSuccess(e)}
                  data-testid="AddNew"
                />
                <CloseIcon
                  color="error"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsEdit(false);
                    setCurrentLabel(label);
                  }}
                  data-testid="CancelNew"
                />
              </Box>
            </Box>
          ) : (
            <Box
              color="primary.main"
              sx={{
                fontWeight: 'inherit',
                flexGrow: 1,
                display: 'flex'
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsEdit(true);
              }}
              data-testid="TypePlaceholder"
            >
              <Box component={AddIcon} color="primary.main" sx={{ mr: 1 }} />
              {addText}
            </Box>
          )}
        </Box>
      }
      {...other}
    />
  );
};

CustomAddTreeItem.propTypes = {
  branchId: PropTypes.string,
  type: PropTypes.string,
  label: PropTypes.string.isRequired
};
